// src/components/documents/DocumentFilters.jsx - V1 ENTERPRISE
// ═══════════════════════════════════════════════════════════════════════════
// 🔍 DOCUMENT FILTERS — Airbnb/Zillow/QuintoAndar Grade
// ═══════════════════════════════════════════════════════════════════════════
// 🔥 ULTRA ROBUSTO:    Filtros combinables + búsqueda instantánea + orden
// 🔥 ULTRA RÁPIDO:     Debounce implícito vía useMemo en el hook padre
// 🔥 ULTRA EFICIENTE:  React.memo + callbacks estables + zero re-renders
// 🔥 ULTRA PROFESIONAL: Diseño limpio con colapsable móvil + atajos de teclado
// ═══════════════════════════════════════════════════════════════════════════

import React, { useCallback, useMemo, memo } from 'react';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════════════════
// 📐 CONFIGURACIÓN DE OPCIONES DE FILTRO
// ═══════════════════════════════════════════════════════════════════════════

const TYPE_OPTIONS = [
  { value: 'all', label: 'Todos os tipos' },
  { value: 'image', label: 'Imagens' },
  { value: 'pdf', label: 'PDFs' },
  { value: 'document', label: 'Documentos' },
  { value: 'large', label: 'Arquivos Cloud' },
];

const SORT_OPTIONS = [
  { value: 'date', label: 'Data' },
  { value: 'title', label: 'Título' },
  { value: 'size', label: 'Tamanho' },
];

const ORDER_OPTIONS = [
  { value: 'desc', label: 'Mais recentes' },
  { value: 'asc', label: 'Mais antigos' },
];

// ═══════════════════════════════════════════════════════════════════════════
// 🎨 SUB-COMPONENTE: SELECT ESTILIZADO
// ═══════════════════════════════════════════════════════════════════════════

const StyledSelect = memo(function StyledSelect({
  label,
  value,
  options,
  onChange,
  isMobile,
}) {
  return (
    <div className="flex-1 min-w-0">
      {label && (
        <label className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mb-1 block">
          {label}
        </label>
      )}
      <select
        value={value}
        onChange={onChange}
        className={`
          w-full border border-slate-200 rounded-xl bg-white
          text-slate-700 font-medium
          hover:border-slate-300 focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400
          outline-none transition-all duration-200 cursor-pointer
          appearance-none bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2394a3b8%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22M6%209l6%206%206-6%22%2F%3E%3C%2Fsvg%3E')]
          bg-no-repeat bg-[right_10px_center] pr-8
          ${isMobile ? 'px-3 py-2.5 text-sm' : 'px-3 py-2.5 text-sm'}
        `}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 🔍 COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const DocumentFilters = memo(function DocumentFilters({
  search,
  onSearchChange,
  filterType,
  onFilterTypeChange,
  filterClient,
  onFilterClientChange,
  filterProperty,
  onFilterPropertyChange,
  sortBy,
  onSortByChange,
  sortOrder,
  onSortOrderChange,
  clients,
  properties,
  isMobile,
  showFilters,
  onToggleFilters,
  onResetFilters,
  selectedCount,
  filteredCount,
  onSelectAll,
  onBulkDelete,
  searchInputRef,
  primaryColor = '#2563eb',
}) {
  // ─── ¿Hay filtros activos? ───────────────────────────────────────────
  const hasActiveFilters = useMemo(
    () =>
      search ||
      filterType !== 'all' ||
      filterClient !== 'all' ||
      filterProperty !== 'all',
    [search, filterType, filterClient, filterProperty]
  );

  // ─── Handlers estables ───────────────────────────────────────────────
  const handleSearchChange = useCallback(
    (e) => onSearchChange(e.target.value),
    [onSearchChange]
  );

  const handleTypeChange = useCallback(
    (e) => onFilterTypeChange(e.target.value),
    [onFilterTypeChange]
  );

  const handleClientChange = useCallback(
    (e) => onFilterClientChange(e.target.value),
    [onFilterClientChange]
  );

  const handlePropertyChange = useCallback(
    (e) => onFilterPropertyChange(e.target.value),
    [onFilterPropertyChange]
  );

  const handleSortByChange = useCallback(
    (e) => onSortByChange(e.target.value),
    [onSortByChange]
  );

  const handleSortOrderChange = useCallback(
    (e) => onSortOrderChange(e.target.value),
    [onSortOrderChange]
  );

  // ═══════════════════════════════════════════════════════════════════════
  // 🎨 RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* HEADER (siempre visible)                                     */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <div className="flex items-center justify-between px-4 py-3">
        {/* Toggle filtros (móvil) */}
        <button
          onClick={onToggleFilters}
          className="flex items-center gap-2 text-slate-700 font-medium hover:text-slate-900 transition-colors"
        >
          <Filter size={16} strokeWidth={1.5} />
          <span className={isMobile ? 'text-sm' : 'text-sm'}>Filtros</span>
          {hasActiveFilters && (
            <span className="w-2 h-2 rounded-full bg-blue-500" />
          )}
          {isMobile && (
            <span className="text-slate-400">
              {showFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>
          )}
        </button>

        {/* Contador + limpiar */}
        <div className="flex items-center gap-3">
          {hasActiveFilters && (
            <span className="text-xs text-slate-400">
              {filteredCount} de {filteredCount} docs
            </span>
          )}
          <button
            onClick={onResetFilters}
            className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1 transition-colors"
          >
            <X size={14} strokeWidth={1.5} />
            <span className="hidden sm:inline">Limpar</span>
          </button>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* CONTENIDO DESPLEGABLE                                        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      {showFilters && (
        <div className="px-4 pb-4 space-y-3">
          {/* Búsqueda */}
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              strokeWidth={1.5}
            />
            <input
              ref={searchInputRef}
              type="text"
              placeholder={isMobile ? 'Buscar...' : 'Buscar por título, arquivo ou descrição...'}
              value={search}
              onChange={handleSearchChange}
              className={`
                w-full pl-9 pr-4 border border-slate-200 rounded-xl
                bg-white text-slate-900 placeholder-slate-400
                focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400
                outline-none transition-all duration-200
                ${isMobile ? 'py-2.5 text-sm' : 'py-2.5 text-sm'}
              `}
              aria-label="Buscar documentos"
            />
          </div>

          {/* Filtros en grid */}
          <div
            className={`grid gap-3 ${
              isMobile
                ? 'grid-cols-1'
                : 'grid-cols-2 lg:grid-cols-4'
            }`}
          >
            {/* Tipo */}
            <StyledSelect
              label="Tipo"
              value={filterType}
              options={TYPE_OPTIONS}
              onChange={handleTypeChange}
              isMobile={isMobile}
            />

            {/* Cliente */}
            <StyledSelect
              label="Cliente"
              value={filterClient}
              options={[
                { value: 'all', label: 'Todos os clientes' },
                ...(clients || []).map((c) => ({
                  value: c.id,
                  label: c.name?.length > 25 ? c.name.slice(0, 25) + '...' : c.name,
                })),
              ]}
              onChange={handleClientChange}
              isMobile={isMobile}
            />

            {/* Propiedad */}
            <StyledSelect
              label="Imóvel"
              value={filterProperty}
              options={[
                { value: 'all', label: 'Todos os imóveis' },
                ...(properties || []).map((p) => ({
                  value: p.id,
                  label: p.title?.length > 25 ? p.title.slice(0, 25) + '...' : p.title,
                })),
              ]}
              onChange={handlePropertyChange}
              isMobile={isMobile}
            />

            {/* Ordenamiento */}
            <div className="flex gap-2">
              <StyledSelect
                label="Ordenar por"
                value={sortBy}
                options={SORT_OPTIONS}
                onChange={handleSortByChange}
                isMobile={isMobile}
              />
              <StyledSelect
                label="Ordem"
                value={sortOrder}
                options={ORDER_OPTIONS}
                onChange={handleSortOrderChange}
                isMobile={isMobile}
              />
            </div>
          </div>

          {/* Barra de acciones masivas */}
          {selectedCount > 0 && (
            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              <button
                onClick={onSelectAll}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                {selectedCount === filteredCount
                  ? 'Desmarcar todos'
                  : `Selecionar todos (${filteredCount})`}
              </button>

              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">
                  {selectedCount} selecionado{selectedCount > 1 ? 's' : ''}
                </span>
                <button
                  onClick={onBulkDelete}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5
                    bg-rose-600 hover:bg-rose-700 active:bg-rose-800
                    text-white text-xs font-semibold
                    rounded-xl transition-all duration-200
                    shadow-sm shadow-rose-200
                    active:scale-95
                  `}
                >
                  <X size={12} strokeWidth={2.5} />
                  Excluir
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════
// 📤 EXPORT
// ═══════════════════════════════════════════════════════════════════════════

DocumentFilters.displayName = 'DocumentFilters';
export default DocumentFilters;